import React from "react";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { dateLocale } from "@/lib/i18n";

/**
 * The inbox's left pane: one row per client, newest conversation first.
 *
 * Presentational — the rows and the selection both come from above (the list is polled
 * once for the whole console, in admin/ConversationsContext.jsx), so this file never
 * fetches and never decides what "unread" means. `practitioner_has_unread` is derived by
 * the server from two integers (`last_client_seq > practitioner_read_seq`), never counted
 * here: a locally recomputed badge is exactly how a dot and a pill start disagreeing.
 */

/**
 * A timestamp a scanning eye can use: today's threads show the CLOCK (the only thing that
 * distinguishes them), older ones show the DATE, and a thread from another year says so.
 * `dateLocale` maps the product language onto a BCP 47 tag — never a hardcoded fr-CA.
 */
export const listTime = (iso, lang) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const locale = dateLocale(lang);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString(
    locale,
    d.getFullYear() === now.getFullYear()
      ? { day: "numeric", month: "short" }
      : { day: "numeric", month: "short", year: "numeric" },
  );
};

export default function ConversationList({ conversations, selectedId, onSelect }) {
  const t = useT("admin");
  const { lang } = useLanguage();

  return (
    <>
      {conversations.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => onSelect(c.id)}
          className={`w-full text-left px-5 py-4 hover:bg-eh-bg/60 transition flex items-start gap-3 ${selectedId === c.id ? "bg-eh-bg" : ""}`}
          data-testid={`admin-conversation-${c.id}`}
        >
          {/* A fixed-width slot whether or not the dot is there, so the column of names
              does not shift as threads are read. */}
          <span className="mt-1.5 w-2 shrink-0">
            {c.practitioner_has_unread && (
              <span
                data-testid={`admin-conversation-${c.id}-unread`}
                aria-label={t("msg.unread")}
                className="block w-2 h-2 rounded-full bg-eh-ochre-deep"
              />
            )}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <div className={`truncate text-eh-ink ${c.practitioner_has_unread ? "font-semibold" : "font-medium"}`}>
                {c.client_name || c.client_email}
              </div>
              <div className="text-[10px] text-eh-muted shrink-0">{listTime(c.last_message_at, lang)}</div>
            </div>
            <div className="text-xs text-eh-muted truncate">{c.client_email}</div>
            <div className="text-sm text-eh-ink/80 mt-1 line-clamp-2">
              {/* Who spoke last is the difference between "waiting on me" and "waiting on
                  them", and the preview alone does not say it. */}
              {c.last_message_author === "practitioner" && (
                <span className="text-eh-muted">{t("msg.you")} : </span>
              )}
              {c.last_message_preview}
            </div>
          </div>
        </button>
      ))}
    </>
  );
}
